import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SupportedTypes, Ticket } from '../../classes/ticket';
import { API_BASE_URL_SERVICE } from '../../kiene-core.config';
import { AppService } from '../../services/app/app.service';
import { KieneBackendApiService } from '../../services/backend-api/kiene-backend-api.service';
import { LocalStorageService } from '../../services/local-storage/local-storage.service';
import { MessageService } from '../../services/message-service/message.service';
import { ViewImageDialogComponent } from './create-ticket-view-image-dialog.component';
import { TicketsystemService } from '../../services/ticketsystem/ticketsystem.service';

export class CreateTicketDialogInput {
    screenshot: string;
    errorObjectUrl: string;
}

@Component({
    selector: 'kiene-create-ticket-dialog',
    templateUrl: './create-ticket-dialog.component.html',
    styleUrls: ['./create-ticket-dialog.component.scss'],
})
export class CreateTicketDialogComponent implements OnInit {
    titelCtrl = new FormControl(undefined, [Validators.required]);
    descriptionCtrl = new FormControl(undefined, [Validators.required]);
    supportTypeCtrl = new FormControl(1, [Validators.required]);

    includeScreenshot = true;
    selectedFiles: File[] = [];
    supportTypes: SupportedTypes[] = [];
    supportTypeSub = new Subscription();
    isSaving = false;
    isMobile: any;

    constructor(
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        public dialogRef: MatDialogRef<CreateTicketDialogComponent>,
        public dialog: MatDialog,
        private router: Router,
        private api: KieneBackendApiService,
        private messageService: MessageService,
        private localStorageService: LocalStorageService,
        private appService: AppService,
        @Inject(MAT_DIALOG_DATA) public data: CreateTicketDialogInput,
        private ticketsystemService: TicketsystemService,
    ) {}

    ngOnInit(): void {
        this.isMobile = this.appService.isMobileScreen;
        this.readSupportTypes();
    }

    createTicket() {
        let fd = new FormData();
        fd.set('beschreibung', this.descriptionCtrl.value);
        fd.set('titel', this.titelCtrl.value);
        fd.set('editiert_von', this.localStorageService.getCurrentUser().benutzer_id.toString());
        fd.set('tickettyp_id', '1');
        fd.set('mandant_id', this.localStorageService.getCurrentMandant().mandant_id.toString());
        fd.set('frontend_url', this.router.url);

        fd.set('kunde_id', this.localStorageService.getCurrentClient()?.kunde_id?.toString());

        if (this.includeScreenshot && this.data?.screenshot) {
            const screenshotFile = this.base64ToFile(this.data.screenshot, 'screenshot.png');
            fd.append('dokument[]', screenshotFile);
        }

        for (let i = 0; i < this.selectedFiles.length; i++) {
            let file = this.selectedFiles[i];
            fd.append('dokument[]', file);
        }
        if (this.data?.errorObjectUrl) {
            fd.set('backend_url', this.data.errorObjectUrl);
        }

        let params = new HttpParams();

        const options = { headers: new HttpHeaders('Phito-Type: fileupload'), params: params, reportProgress: true };

        this.isSaving = true;
        this.api.postFormData(this.apiBaseUrl + 'ticket/create.php', fd, options).subscribe({
            next: (response: Ticket) => {
                this.messageService.infoMessage('Ticket mit ID: ' + response.ticket_id + ' wurde angelegt');
                this.isSaving = false;
                this.ticketsystemService.setNeuesTicketErstellt(response);
                this.dialogRef.close(response);
            },
            error: error => {
                this.isSaving = false;
                this.messageService.errorMessage('Ticket konnte nicht erstellt werden! ', error);
            },
        });
    }

    base64ToFile(base64: string, filename: string): File {
        const arr = base64.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }

        return new File([u8arr], filename, { type: mime });
    }

    uploadFiles() {
        const fileInput = document.getElementById('fileInput') as HTMLInputElement;
        fileInput.click();
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files) {
            const newFiles = Array.from(input.files);
            const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf', 'csv', 'xls', 'xlsx'];

            const validFiles = newFiles.filter(file => {
                const fileExtension = file.name.split('.').pop()?.toLowerCase();
                return fileExtension && allowedExtensions.includes(fileExtension);
            });

            if (validFiles.length < newFiles.length) {
                alert('Einige Dateien wurden nicht hinzugefügt, da sie nicht den erlaubten Formaten entsprechen.');
            }

            this.selectedFiles = [...this.selectedFiles, ...validFiles];
        }
    }

    openImageDialog(imageSrc: string): void {
        this.dialog.open(ViewImageDialogComponent, { data: { imageSrc } });
    }

    isValid() {
        return !this.isSaving && this.titelCtrl.valid && this.descriptionCtrl.valid && this.supportTypeCtrl.valid;
    }

    removeFile(file: File): void {
        const index = this.selectedFiles.indexOf(file);
        if (index >= 0) {
            this.selectedFiles.splice(index, 1);
        }
    }

    readSupportTypes() {
        this.supportTypeSub?.unsubscribe();
        this.supportTypeSub = this.api.getAll(this.apiBaseUrl + 'ticket_typ/read.php').subscribe({
            next: response => {
                this.supportTypes = response.records;
                this.supportTypeSub?.unsubscribe();
            },
            error: error => {
                this.messageService.errorMessage('Fehler beim Laden der Support-Typen!', error);
                this.supportTypeSub?.unsubscribe();
            },
        });
    }
}
